/**
 * Broker Concentration Analysis Utilities
 *
 * Provides functions to calculate broker revenue percentages and detect concentration risks.
 * Used by: DashboardPage, OfficeDashboardPage, BookHealthReportPage
 */

interface BrokerData {
  brokerDisplay: string;
  brokerId?: string;
  upfrontNet: number;
  trailNet: number;
}

export interface BrokerRevenue {
  brokerId: string;
  brokerName: string;
  upfrontCents: number;
  trailCents: number;
  totalCents: number;
  percentOfTotal: number;
}

/**
 * Calculate broker revenue percentages from byBroker breakdown
 *
 * @param byBroker - Record of broker data from monthly aggregates
 * @returns Array of broker revenue objects sorted by total (descending)
 */
export const calculateBrokerPercentages = (
  byBroker: Record<string, BrokerData>
): BrokerRevenue[] => {
  if (!byBroker || Object.keys(byBroker).length === 0) {
    return [];
  }

  const entries = Object.entries(byBroker);

  // Calculate grand total across all brokers
  const grandTotal = entries.reduce(
    (sum, [_, data]) => sum + (data.upfrontNet || 0) + (data.trailNet || 0),
    0
  );

  // Map to BrokerRevenue objects with percentages
  const brokerRevenues: BrokerRevenue[] = entries.map(([brokerId, data]) => {
    const upfrontCents = data.upfrontNet || 0;
    const trailCents = data.trailNet || 0;
    const total = upfrontCents + trailCents;

    return {
      brokerId,
      brokerName: data.brokerDisplay || brokerId,
      upfrontCents,
      trailCents,
      totalCents: total,
      percentOfTotal: grandTotal > 0 ? (total / grandTotal) * 100 : 0,
    };
  });

  // Sort by total revenue (descending)
  return brokerRevenues.sort((a, b) => b.totalCents - a.totalCents);
};

/**
 * Calculate broker revenue percentages across all months
 * Aggregates all monthly byBroker data into a single set of percentages
 *
 * @param aggregates - Array of monthly aggregates with byBroker breakdowns
 * @returns Array of broker revenue objects sorted by total (descending)
 */
export const calculateBrokerPercentagesAcrossMonths = (
  aggregates: Array<any>
): BrokerRevenue[] => {
  // Combine all monthly byBroker data into a single map
  const combinedByBroker: Record<string, BrokerData> = {};

  aggregates.forEach((agg) => {
    if (!agg.byBroker) return;

    Object.entries(agg.byBroker).forEach(([brokerId, data]: [string, any]) => {
      if (!combinedByBroker[brokerId]) {
        combinedByBroker[brokerId] = {
          brokerDisplay: data.brokerDisplay || brokerId,
          brokerId,
          upfrontNet: 0,
          trailNet: 0,
        };
      }

      combinedByBroker[brokerId].upfrontNet += data.upfrontNet || 0;
      combinedByBroker[brokerId].trailNet += data.trailNet || 0;
    });
  });

  return calculateBrokerPercentages(combinedByBroker);
};

/**
 * Detect concentration risk - find broker with highest percentage
 *
 * @param brokers - Array of broker revenue objects
 * @param threshold - Percentage threshold for concentration risk (default 25%)
 * @returns Broker with highest concentration if above threshold, null otherwise
 */
export const getConcentrationRisk = (
  brokers: BrokerRevenue[],
  threshold = 25
): BrokerRevenue | null => {
  if (!brokers || brokers.length === 0) return null;

  // Brokers are already sorted by totalCents descending
  const topBroker = brokers[0];

  return topBroker.percentOfTotal > threshold ? topBroker : null;
};

/**
 * Filter aggregates' byBroker breakdown by broker activity status.
 * Returns new aggregates array with byBroker filtered to only include
 * brokers matching the specified status. Percentages are then computed
 * correctly among the filtered set by calculateBrokerPercentagesAcrossMonths.
 */
export const filterAggregatesByBrokerStatus = (
  aggregates: Array<any>,
  brokerStatuses: Map<string, 'active' | 'inactive'>,
  targetStatus: 'active' | 'inactive'
): Array<any> => {
  return aggregates.map(agg => {
    if (!agg.byBroker) return agg;

    const filteredByBroker: Record<string, BrokerData> = {};

    Object.entries(agg.byBroker).forEach(([brokerId, data]: [string, any]) => {
      const brokerDisplay = data.brokerDisplay || brokerId;
      const status = brokerStatuses.get(brokerDisplay) || 'active';

      if (status === targetStatus) {
        filteredByBroker[brokerId] = data;
      }
    });

    return { ...agg, byBroker: filteredByBroker };
  });
};

/**
 * Prepare pie chart data - group small brokers as "Other"
 *
 * @param brokers - Array of broker revenue objects
 * @param threshold - Minimum percentage to show individually (default 5%)
 * @returns Array of pie chart data points
 */
export const getPieChartData = (
  brokers: BrokerRevenue[],
  threshold = 5
): Array<{ name: string; value: number; percent: number }> => {
  if (!brokers || brokers.length === 0) return [];

  const significant = brokers.filter(b => b.percentOfTotal >= threshold);
  const otherBrokers = brokers.filter(b => b.percentOfTotal < threshold);

  const data = significant.map(b => ({
    name: b.brokerName,
    value: b.totalCents,
    percent: b.percentOfTotal,
  }));

  if (otherBrokers.length > 0) {
    const otherTotal = otherBrokers.reduce((sum, b) => sum + b.totalCents, 0);
    const otherPercent = otherBrokers.reduce((sum, b) => sum + b.percentOfTotal, 0);

    data.push({
      name: 'Other',
      value: otherTotal,
      percent: otherPercent,
    });
  }

  return data;
};
